import { AutoAwesome, CloudDownload } from '@mui/icons-material';
import DisabledHint from '~/components/Tooltips/DisabledHint';
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    Typography,
} from '@mui/material';
import { ReactNode, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import ImageItem from '~/components/ImageItem/ImageItem';
import SensitiveInfoTypography from '~/components/SensitiveInfoTypography';
import { isCompositeCbctEligible } from '~/hooks/image/useCompositeCbctEligibility';
import useLoadAvailableImage from '~/hooks/image/useLoadAvailableImage';
import { useImagesCompletenessGate } from '~/hooks/image/useImageStreamProgress';
import usePrimaryImage, { AvailableImage } from '~/hooks/image/usePrimaryImage';
import useBusyIndicator from '~/hooks/useBusyIndicator';
import useCompositeCbctTask from '~/hooks/workflows/useCompositeCbctTask';
import { handleError } from '~/utils/errors';
import { cutUID } from '~/utils/string';
import { outlinedPickerSx } from '~/theme/outlinedPickerSx';

type CompositeCbctDialogProps = {
    open: boolean;
    onClose: () => void;
};

function renderOption(image: AvailableImage): ReactNode {
    // Loaded images get the rich ImageItem (modality chip, slice
    // count, thickness, tooltip). Session-only entries have no
    // Image3D yet so ImageItem cannot render them — fall back to a
    // compact row that surfaces the modality, label, and a
    // "will load from PACS on start" hint so the user understands
    // why the option looks different.
    if (image.imageObject) {
        return (
            <Box width="100%" overflow="hidden">
                <ImageItem image={image.imageObject} />
                <Typography
                    fontSize={10}
                    color="text.secondary"
                    noWrap
                    mt={0.25}
                >
                    <SensitiveInfoTypography>
                        {image.seriesInstanceUID}
                    </SensitiveInfoTypography>
                </Typography>
            </Box>
        );
    }
    return (
        <Box width="100%" overflow="hidden">
            <Box display="flex" alignItems="center" gap={1}>
                <CloudDownload
                    fontSize="small"
                    color="action"
                    aria-hidden="true"
                />
                <Typography fontSize={13}>
                    {image.modality} —{' '}
                    {image.label || (
                        <FormattedMessage id="compositeCbctOptionNoLabel" />
                    )}
                </Typography>
            </Box>
            <Typography fontSize={10} color="text.secondary" noWrap mt={0.25}>
                <SensitiveInfoTypography>
                    {image.seriesInstanceUID}
                </SensitiveInfoTypography>
            </Typography>
            <Typography fontSize={10} color="warning.main" noWrap>
                <FormattedMessage id="compositeCbctSessionOnlyHint" />
            </Typography>
        </Box>
    );
}

function renderSelectedValue(
    image: AvailableImage | undefined,
    placeholderId: string
): ReactNode {
    if (!image) {
        return (
            <Typography fontSize={14} color="text.secondary">
                <FormattedMessage id={placeholderId} />
            </Typography>
        );
    }
    const hasLabel = !!image.label;
    return hasLabel ? (
        <SensitiveInfoTypography>
            <Typography
                component="span"
                fontSize={14}
                color="white"
                fontStyle="normal"
                sx={{
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                }}
            >
                {image.label}
            </Typography>
        </SensitiveInfoTypography>
    ) : (
        <Typography component="span">
            {image.modality} -{' '}
            <SensitiveInfoTypography>
                {cutUID(image.seriesInstanceUID)}
            </SensitiveInfoTypography>
        </Typography>
    );
}

export default function CompositeCbctDialog({
    open,
    onClose,
}: CompositeCbctDialogProps) {
    const { availableImages } = usePrimaryImage(true);
    const loadAvailableImage = useLoadAvailableImage();
    const { showBusy, hideBusy } = useBusyIndicator();
    const [cbctSeriesUid, setCbctSeriesUid] = useState<string>('');
    const [baseCtSeriesUid, setBaseCtSeriesUid] = useState<string>('');
    const [isPreparing, setIsPreparing] = useState(false);

    const eligibleImages = useMemo(
        () => availableImages.filter(isCompositeCbctEligible),
        [availableImages]
    );

    const cbctImage = useMemo(
        () =>
            eligibleImages.find(
                (img) => img.seriesInstanceUID === cbctSeriesUid
            ),
        [eligibleImages, cbctSeriesUid]
    );
    const baseCtImage = useMemo(
        () =>
            eligibleImages.find(
                (img) => img.seriesInstanceUID === baseCtSeriesUid
            ),
        [eligibleImages, baseCtSeriesUid]
    );

    const cbctOptions = useMemo(
        () =>
            eligibleImages.filter(
                (img) => img.seriesInstanceUID !== baseCtSeriesUid
            ),
        [eligibleImages, baseCtSeriesUid]
    );
    const baseCtOptions = useMemo(
        () =>
            eligibleImages.filter(
                (img) => img.seriesInstanceUID !== cbctSeriesUid
            ),
        [eligibleImages, cbctSeriesUid]
    );

    const handleClose = () => {
        setCbctSeriesUid('');
        setBaseCtSeriesUid('');
        onClose();
    };

    const { startProcessing, isSubmitting } = useCompositeCbctTask(handleClose);

    // Composing ships both series' slices; a partial CBCT or base CT would
    // produce a synthetic image covering only the part that had arrived.
    const imageGate = useImagesCompletenessGate([
        cbctImage?.imageObject,
        baseCtImage?.imageObject,
    ]);

    const isWorking = isSubmitting || isPreparing;
    const canStart =
        !!cbctImage && !!baseCtImage && !isWorking && !imageGate.isBlocked;

    const handleStart = async () => {
        if (!cbctImage || !baseCtImage || isWorking) {
            return;
        }
        // In compatibility mode the connector only indexed the
        // session's ContextImage, so any session-only input must be
        // pulled from PACS before submission. `loadAvailableImage` is a
        // no-op for inputs that are already loaded.
        const needsLoad = !cbctImage.imageObject || !baseCtImage.imageObject;
        if (needsLoad) {
            setIsPreparing(true);
            await showBusy('Downloading images...');
            try {
                await Promise.all([
                    loadAvailableImage(cbctImage),
                    loadAvailableImage(baseCtImage),
                ]);
            } catch (error) {
                handleError(error, 'Failed to download Composite CBCT inputs.');
                return;
            } finally {
                hideBusy();
                setIsPreparing(false);
            }
        }
        await startProcessing({ cbct: cbctImage, baseCt: baseCtImage });
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
            data-testid="composite-cbct-dialog"
        >
            <DialogTitle>
                <FormattedMessage id="compositeCbct" />
            </DialogTitle>
            <DialogContent>
                <Box display="flex" flexDirection="column" gap={2} pt={1}>
                    <Typography variant="body2">
                        <FormattedMessage id="compositeCbctDescription" />
                    </Typography>

                    {eligibleImages.length < 2 ? (
                        <Typography variant="body2" color="warning.main">
                            <FormattedMessage id="compositeCbctEligibleImagesRequired" />
                        </Typography>
                    ) : (
                        <>
                            <FormControl
                                fullWidth
                                sx={outlinedPickerSx(
                                    cbctImage ? 'primary' : 'warning'
                                )}
                            >
                                <InputLabel
                                    shrink
                                    id="composite-cbct-cbct-label"
                                    size="small"
                                >
                                    <FormattedMessage id="cbctImage" />
                                </InputLabel>
                                <Select
                                    notched
                                    data-testid="compositeCbctCbctSelect"
                                    labelId="composite-cbct-cbct-label"
                                    id="composite-cbct-cbct-select"
                                    label="CBCT image"
                                    value={cbctSeriesUid}
                                    displayEmpty
                                    renderValue={() =>
                                        renderSelectedValue(
                                            cbctImage,
                                            'selectCbctImage'
                                        )
                                    }
                                    onChange={(event) =>
                                        setCbctSeriesUid(
                                            String(event.target.value ?? '')
                                        )
                                    }
                                    size="small"
                                    MenuProps={{
                                        PaperProps: { sx: { mt: 0.5 } },
                                    }}
                                >
                                    {cbctOptions.map((image) => (
                                        <MenuItem
                                            key={image.seriesInstanceUID}
                                            value={image.seriesInstanceUID}
                                        >
                                            {renderOption(image)}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            <FormControl
                                fullWidth
                                sx={outlinedPickerSx(
                                    baseCtImage ? 'primary' : 'warning'
                                )}
                            >
                                <InputLabel
                                    shrink
                                    id="composite-cbct-basect-label"
                                    size="small"
                                >
                                    <FormattedMessage id="baseCtImage" />
                                </InputLabel>
                                <Select
                                    notched
                                    data-testid="compositeCbctBaseCtSelect"
                                    labelId="composite-cbct-basect-label"
                                    id="composite-cbct-basect-select"
                                    label="Base CT image"
                                    value={baseCtSeriesUid}
                                    displayEmpty
                                    renderValue={() =>
                                        renderSelectedValue(
                                            baseCtImage,
                                            'selectBaseCtImage'
                                        )
                                    }
                                    onChange={(event) =>
                                        setBaseCtSeriesUid(
                                            String(event.target.value ?? '')
                                        )
                                    }
                                    size="small"
                                    MenuProps={{
                                        PaperProps: { sx: { mt: 0.5 } },
                                    }}
                                >
                                    {baseCtOptions.map((image) => (
                                        <MenuItem
                                            key={image.seriesInstanceUID}
                                            value={image.seriesInstanceUID}
                                        >
                                            {renderOption(image)}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </>
                    )}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} disabled={isWorking}>
                    <FormattedMessage id="cancel" />
                </Button>
                <DisabledHint title={imageGate.reason} placement="bottom">
                    <Button
                        variant="contained"
                        endIcon={<AutoAwesome />}
                        onClick={handleStart}
                        disabled={!canStart}
                        data-testid="compositeCbctStartButton"
                    >
                        <FormattedMessage id="start" />
                    </Button>
                </DisabledHint>
            </DialogActions>
        </Dialog>
    );
}
